// app/javascript/controllers/dae20/pipeline_controller.js
import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

/**
 * Stimulus Controller для pipeline компонента.
 *
 * Функции:
 * - Подписка на ActionCable (PipelineChannel) для real-time прогресса
 * - Plan: запрос оценки scope через GET /dae20/pipelines/:id/plan
 * - Execute: запуск pipeline через POST /dae20/pipelines/:id/execute
 * - Save: сохранение JSON-конфига через PATCH /dae20/pipelines/:id
 *
 * Использование:
 * <div data-controller="dae20--pipeline"
 *      data-dae20--pipeline-component-id-value="123">
 *   ...
 * </div>
 */
export default class extends Controller {
  static targets = [
    "configEditor",    // textarea с JSON конфигом
    "planResult",      // div для отображения результата Plan
    "progressBar",     // div для прогресс-бара
    "progressText",    // span для текста прогресса
    "statusBadge",     // span для статуса
    "runResult",       // div для результата запуска
    "errorLog"         // div для error log
  ]

  static values = {
    componentId: String
  }

  subscription = null
  consumer = null

  connect() {
    if (!this.componentIdValue) {
      console.warn("[Pipeline] No componentId — skipping")
      return
    }
    this.subscribe()
    this.loadStatus()
  }

  disconnect() {
    this.unsubscribe()
  }

  // === ActionCable ===

  subscribe() {
    if (this.subscription) return

    try {
      this.consumer = this.consumer || createConsumer()
      this.subscription = this.consumer.subscriptions.create(
        {
          channel: "Dae20::PipelineChannel",
          component_id: this.componentIdValue
        },
        {
          connected: () => console.log("[Pipeline] Subscribed"),
          disconnected: () => console.log("[Pipeline] Disconnected"),
          received: (data) => this.handleReceived(data),
          rejected: () => console.warn("[Pipeline] Subscription rejected")
        }
      )
    } catch (error) {
      console.error("[Pipeline] Subscribe error:", error)
    }
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleReceived(data) {
    if (data.type === "pipeline_progress") {
      this.updateProgress(data.run)
    }
  }

  // === Actions ===

  async plan() {
    try {
      this.setPlanResult("Планирование...")

      const url = `/dae20/pipelines/${this.componentIdValue}/plan`
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      const data = await response.json()

      if (data.ok) {
        let html = `<div class="text-sm space-y-2">`
        html += `<p><strong>Записей:</strong> ${data.total_rows}</p>`

        if (data.step_summaries && data.step_summaries.length > 0) {
          html += `<p><strong>Шаги:</strong></p><ul class="list-disc pl-4">`
          data.step_summaries.forEach(s => {
            html += `<li>${s.name}: ${s.description}</li>`
          })
          html += `</ul>`
        }

        if (data.preview_rows && data.preview_rows.length > 0) {
          html += `<p><strong>Превью (первые ${data.preview_rows.length}):</strong></p>`
          html += `<pre class="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">${JSON.stringify(data.preview_rows, null, 2)}</pre>`
        }

        html += `</div>`
        this.setPlanResult(html)
      } else {
        this.setPlanResult(`<p class="text-red-600">Ошибка: ${data.errors.join(", ")}</p>`)
      }
    } catch (error) {
      this.setPlanResult(`<p class="text-red-600">Ошибка: ${error.message}</p>`)
    }
  }

  async execute() {
    try {
      this.setStatus("running")
      this.updateProgress({ status: "pending", processed_rows: 0, total_rows: 0, progress_percent: 0 })

      const url = `/dae20/pipelines/${this.componentIdValue}/execute`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })
      const data = await response.json()

      if (!data.ok) {
        this.setRunResult(`<p class="text-red-600">Ошибка запуска: ${data.error || "unknown"}</p>`)
      }
    } catch (error) {
      this.setRunResult(`<p class="text-red-600">Ошибка: ${error.message}</p>`)
    }
  }

  async saveConfig() {
    try {
      if (!this.hasConfigEditorTarget) return

      const raw = this.configEditorTarget.value
      let config
      try {
        config = JSON.parse(raw)
      } catch (e) {
        alert("Невалидный JSON: " + e.message)
        return
      }

      const url = `/dae20/pipelines/${this.componentIdValue}`
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin",
        body: JSON.stringify({ pipeline: config })
      })
      const data = await response.json()

      if (data.ok) {
        this.setStatus("ready")
      } else {
        alert("Ошибка сохранения: " + (data.error || "unknown"))
      }
    } catch (error) {
      alert("Ошибка: " + error.message)
    }
  }

  async loadStatus() {
    try {
      const url = `/dae20/pipelines/${this.componentIdValue}/status`
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      const data = await response.json()

      if (data.pipeline) {
        this.setStatus(data.pipeline.status)

        // Заполнить editor текущим конфигом
        if (this.hasConfigEditorTarget && data.pipeline.steps) {
          this.configEditorTarget.value = JSON.stringify(data.pipeline, null, 2)
        }
      }

      if (data.runs && data.runs.length > 0) {
        const lastRun = data.runs[0]
        this.updateProgress(lastRun)

        if (lastRun.error_log && lastRun.error_log.length > 0) {
          this.setErrorLog(lastRun.error_log)
        }
      }
    } catch (error) {
      console.warn("[Pipeline] loadStatus error:", error)
    }
  }

  // === UI Updates ===

  updateProgress(run) {
    if (this.hasProgressBarTarget) {
      const pct = run.progress_percent || 0
      this.progressBarTarget.style.width = `${pct}%`
    }

    if (this.hasProgressTextTarget) {
      const parts = []
      if (run.processed_rows !== undefined) parts.push(`${run.processed_rows}/${run.total_rows || "?"}`)
      if (run.succeeded_rows !== undefined) parts.push(`ok: ${run.succeeded_rows}`)
      if (run.failed_rows > 0) parts.push(`err: ${run.failed_rows}`)
      if (run.duration_seconds) parts.push(`${run.duration_seconds}s`)
      this.progressTextTarget.textContent = parts.join(" | ")
    }

    if (run.status) {
      this.setStatus(run.status)
    }

    // Show run result when done
    if (["done", "failed", "cancelled"].includes(run.status)) {
      let html = `<div class="text-sm">`
      html += `<p>Статус: <strong>${run.status}</strong></p>`
      html += `<p>Обработано: ${run.processed_rows}/${run.total_rows}</p>`
      html += `<p>Успешно: ${run.succeeded_rows}, Ошибки: ${run.failed_rows}</p>`
      if (run.duration_seconds) html += `<p>Время: ${run.duration_seconds}s</p>`
      html += `</div>`
      this.setRunResult(html)
    }
  }

  setStatus(status) {
    if (!this.hasStatusBadgeTarget) return

    const colors = {
      draft: "bg-gray-100 text-gray-700",
      ready: "bg-blue-100 text-blue-700",
      running: "bg-yellow-100 text-yellow-700",
      executing: "bg-yellow-100 text-yellow-700",
      planning: "bg-blue-100 text-blue-700",
      done: "bg-green-100 text-green-700",
      failed: "bg-red-100 text-red-700"
    }

    this.statusBadgeTarget.className = `inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || colors.draft}`
    this.statusBadgeTarget.textContent = status
  }

  setPlanResult(html) {
    if (this.hasPlanResultTarget) {
      this.planResultTarget.innerHTML = html
    }
  }

  setRunResult(html) {
    if (this.hasRunResultTarget) {
      this.runResultTarget.innerHTML = html
    }
  }

  setErrorLog(errors) {
    if (!this.hasErrorLogTarget) return

    if (!errors || errors.length === 0) {
      this.errorLogTarget.innerHTML = ""
      return
    }

    let html = `<details class="text-xs mt-2"><summary class="cursor-pointer text-red-600">Ошибки (${errors.length})</summary>`
    html += `<ul class="list-disc pl-4 mt-1 space-y-0.5">`
    errors.forEach(e => {
      html += `<li>row=${e.row_id || "?"} step=${e.step || "?"}: ${e.error || "?"}</li>`
    })
    html += `</ul></details>`
    this.errorLogTarget.innerHTML = html
  }

  // === Helpers ===

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
